/**
 * A checklist of "ripples" — the work a roadmap item sets off: tick, edit in place, change owner,
 * drag to reorder, add, remove. The module keeps the items and says what each change does.
 *
 * Ticks, owners and reorders are quick edits — the caller applies them at once and rolls back
 * on failure (see useQuickEdit). Adding and editing wait for the server: the form stays open,
 * with what was typed, until the save lands, so a refused save loses nothing.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, GripVertical, Pencil, X } from 'lucide-react';
import { cn } from '../lib/utils';
import type { ThreadPerson } from './comment-thread';

export interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  ownerUserId: string | null;
  done: boolean;
}

export interface NewChecklistItem {
  title: string;
  description: string;
  ownerUserId: string | null;
}

const META = 'text-[hsl(var(--ns-boulder))]';
const DECOR = 'text-[hsl(var(--ns-concrete))]';
const ON_ACAI = 'text-[hsl(var(--ns-unbleached))]';

const UNASSIGNED = 'unassigned';

interface Props<I extends ChecklistItem> {
  items: readonly I[];
  directory: readonly ThreadPerson[];
  canEdit: boolean;
  /** Who a new ripple is for until someone picks: usually the item's owner. */
  defaultOwnerUserId: string | null;
  /** Resolves true once saved; the composer closes only then. */
  onAdd: (input: NewChecklistItem) => Promise<boolean>;
  /** Resolves true once saved; the editor closes only then. */
  onEdit: (id: string, input: { title: string; description: string }) => Promise<boolean>;
  /** Tick or owner: applied at once by the caller. */
  onQuick: (id: string, input: { done?: boolean; ownerUserId?: string | null }) => void;
  onRemove: (id: string) => void;
  onReorder: (ids: string[]) => void;
}

const fieldClass =
  'rounded-lg border border-[hsl(var(--ns-acai)/0.2)] bg-background px-2.5 py-2 outline-none focus:border-primary';

export function Checklist<I extends ChecklistItem>({
  items: ripples,
  directory,
  canEdit,
  defaultOwnerUserId,
  onAdd,
  onEdit,
  onQuick,
  onRemove,
  onReorder,
}: Props<I>) {
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const active = directory.filter((user) => user.active);

  const moveTo = (targetId: string) => {
    if (dragId === null || dragId === targetId) return;
    const ids = ripples.map((ripple) => ripple.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ...ids.splice(from, 1));
    onReorder(ids);
  };

  return (
    <>
      {ripples.map((ripple) => {
        const editing = editingId === ripple.id && canEdit;
        return (
          <div
            key={ripple.id}
            draggable={canEdit && !editing}
            onDragStart={(event) => {
              // Firefox won't start a drag without data.
              event.dataTransfer.setData('text/plain', ripple.id);
              setDragId(ripple.id);
            }}
            onDragEnd={() => {
              setDragId(null);
              setOverId(null);
            }}
            onDragOver={(event) => {
              if (dragId === null) return;
              event.preventDefault();
              if (overId !== ripple.id) setOverId(ripple.id);
            }}
            onDrop={(event) => {
              if (dragId === null) return;
              event.preventDefault();
              moveTo(ripple.id);
              setDragId(null);
              setOverId(null);
            }}
            className={cn(
              'flex items-start gap-3 rounded-xl bg-background py-4 pl-3 pr-4',
              dragId === ripple.id && 'opacity-40',
              overId === ripple.id &&
                dragId !== null &&
                dragId !== ripple.id &&
                'shadow-[0_0_0_2px_hsl(var(--ns-acai))]',
            )}
          >
            {canEdit && (
              <GripVertical
                className={cn('mt-[3px] size-4 shrink-0 cursor-grab', DECOR)}
                aria-hidden
              />
            )}
            <Tick
              done={ripple.done}
              label={ripple.title}
              disabled={!canEdit}
              onToggle={() => onQuick(ripple.id, { done: !ripple.done })}
            />
            {editing ? (
              <RippleEditor
                ripple={ripple}
                onSave={async (input) => {
                  const ok = await onEdit(ripple.id, input);
                  if (ok) setEditingId(null);
                  return ok;
                }}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <>
                <div
                  className={cn('flex min-w-0 flex-1 flex-col gap-1', ripple.done && 'opacity-55')}
                >
                  <p
                    className={cn(
                      'text-[17px] font-semibold leading-[1.2]',
                      ripple.done && 'line-through',
                    )}
                  >
                    {ripple.title}
                  </p>
                  {ripple.description !== '' && (
                    <p className="text-pretty text-[15px] leading-[1.4]">{ripple.description}</p>
                  )}
                </div>
                {canEdit ? (
                  <select
                    value={ripple.ownerUserId ?? UNASSIGNED}
                    onChange={(event) =>
                      onQuick(ripple.id, {
                        ownerUserId: event.target.value === UNASSIGNED ? null : event.target.value,
                      })
                    }
                    aria-label={`Owner of ${ripple.title}`}
                    className="max-w-40 shrink-0 cursor-pointer appearance-none bg-transparent text-right text-[15px] outline-none"
                  >
                    <option value={UNASSIGNED}>Unassigned</option>
                    {directory
                      .filter((user) => user.active || user.id === ripple.ownerUserId)
                      .map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                  </select>
                ) : (
                  <span className="shrink-0 text-[15px]">
                    {directory.find((user) => user.id === ripple.ownerUserId)?.name ?? 'Unassigned'}
                  </span>
                )}
                {canEdit && (
                  <span className="flex shrink-0 gap-0.5">
                    <IconAction
                      label={`Edit ripple: ${ripple.title}`}
                      title="Edit ripple"
                      onClick={() => setEditingId(ripple.id)}
                    >
                      <Pencil className="size-4" strokeWidth={1.75} />
                    </IconAction>
                    <IconAction
                      label={`Remove ripple: ${ripple.title}`}
                      title="Remove ripple"
                      onClick={() => onRemove(ripple.id)}
                    >
                      <X className="size-4" strokeWidth={1.75} />
                    </IconAction>
                  </span>
                )}
              </>
            )}
          </div>
        );
      })}

      {canEdit &&
        (composerOpen ? (
          <RippleComposer
            directory={active}
            defaultOwnerUserId={defaultOwnerUserId}
            onAdd={async (input) => {
              const ok = await onAdd(input);
              if (ok) setComposerOpen(false);
              return ok;
            }}
            onCancel={() => setComposerOpen(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            className={cn(
              'h-12 rounded-xl border-[1.5px] border-dashed border-[hsl(var(--ns-acai)/0.22)] bg-transparent text-base hover:text-[hsl(var(--ns-acai))]',
              META,
            )}
          >
            + Add ripple
          </button>
        ))}
    </>
  );
}

/** The prototype's 22 px checkbox: Açaí fill and a cream tick when done. */
function Tick({
  done,
  label,
  disabled,
  onToggle,
}: {
  done: boolean;
  label: string;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={done}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        'mt-px grid size-[22px] shrink-0 place-items-center rounded-[5px] border-[1.5px] p-0 disabled:cursor-default',
        done
          ? cn('border-primary bg-primary', ON_ACAI)
          : 'border-[hsl(var(--ns-acai)/0.35)] bg-background',
      )}
    >
      {done && <Check className="size-3.5" strokeWidth={2.5} />}
    </button>
  );
}

function IconAction({
  label,
  title,
  onClick,
  children,
}: {
  label: string;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={title}
      className={cn(
        'grid size-8 place-items-center rounded-lg hover:bg-[hsl(var(--ns-pebble)/0.4)] hover:text-[hsl(var(--ns-acai))]',
        DECOR,
      )}
    >
      {children}
    </button>
  );
}

function SmallButton({
  primary = false,
  disabled = false,
  onClick,
  children,
}: {
  primary?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'h-[34px] whitespace-nowrap rounded-lg px-3 text-sm disabled:opacity-50',
        primary
          ? cn('bg-primary', ON_ACAI)
          : 'border border-[hsl(var(--ns-acai)/0.14)] bg-background',
      )}
    >
      {children}
    </button>
  );
}

/** Title and description; the owner stays on the row's own select. */
function RippleEditor({
  ripple,
  onSave,
  onCancel,
}: {
  ripple: ChecklistItem;
  onSave: (input: { title: string; description: string }) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(ripple.title);
  const [description, setDescription] = useState(ripple.description);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle(ripple.title);
    setDescription(ripple.description);
  }, [ripple.title, ripple.description]);

  // A ref as well as the state: Enter and a click in one tick would both see `saving` false.
  const inFlight = useRef(false);
  const submit = async () => {
    if (title.trim() === '' || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    try {
      await onSave({ title: title.trim(), description: description.trim() });
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-2">
      <input
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => event.key === 'Enter' && submit()}
        aria-label="Ripple title"
        autoFocus
        className={cn(fieldClass, 'text-base font-semibold')}
      />
      <textarea
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        rows={2}
        aria-label="Ripple description"
        className={cn(fieldClass, 'resize-y text-[15px]')}
      />
      <div className="flex justify-end gap-2">
        <SmallButton onClick={onCancel}>Cancel</SmallButton>
        <SmallButton primary onClick={submit} disabled={saving || title.trim() === ''}>
          Save
        </SmallButton>
      </div>
    </div>
  );
}

function RippleComposer({
  directory,
  defaultOwnerUserId,
  onAdd,
  onCancel,
}: {
  directory: readonly ThreadPerson[];
  defaultOwnerUserId: string | null;
  onAdd: (input: NewChecklistItem) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ownerUserId, setOwnerUserId] = useState(
    defaultOwnerUserId !== null && directory.some((user) => user.id === defaultOwnerUserId)
      ? defaultOwnerUserId
      : UNASSIGNED,
  );

  // See RippleEditor: the ref is the guard, the state drives the button.
  const inFlight = useRef(false);
  const submit = async () => {
    if (title.trim() === '' || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    try {
      await onAdd({
        title: title.trim(),
        description: description.trim(),
        ownerUserId: ownerUserId === UNASSIGNED ? null : ownerUserId,
      });
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-background p-4">
      <input
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => event.key === 'Enter' && submit()}
        placeholder="Ripple title"
        aria-label="Ripple title"
        autoFocus
        className={cn(fieldClass, 'text-base font-semibold placeholder:font-normal')}
      />
      <textarea
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        rows={2}
        placeholder="What needs to happen"
        aria-label="Ripple description"
        className={cn(fieldClass, 'resize-y text-[15px]')}
      />
      <div className="flex items-center gap-2">
        <select
          value={ownerUserId}
          onChange={(event) => setOwnerUserId(event.target.value)}
          aria-label="Ripple owner"
          className="h-[34px] rounded-lg border border-[hsl(var(--ns-acai)/0.2)] bg-background px-2 text-sm outline-none focus:border-primary"
        >
          <option value={UNASSIGNED}>Unassigned</option>
          {directory.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
        <span className="flex-1" />
        <SmallButton onClick={onCancel}>Cancel</SmallButton>
        <SmallButton primary onClick={submit} disabled={saving || title.trim() === ''}>
          Add ripple
        </SmallButton>
      </div>
    </div>
  );
}
